'use client';

import {
  Dialog,
  Flex,
  Text,
  Toggle,
  ButtonGroup,
  Button,
  Alert,
  Pill,
} from '@/_components';
import { TaxReturnSignatureDialogProps } from './TaxReturnSignatureDialog.types';
import { useContext, useState } from 'react';
import { AdminTaxReturnsContext } from '../../AdminTaxReturnsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { updateTaxReturn } from '@/_api/admin-tax-returns';
import type { TaxReturn } from '@/_types/models/taxReturns';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';

export const TaxReturnSignatureDialog = ({
  isOpen,
  onClose,
}: TaxReturnSignatureDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    selectedSignaturesReturn,
    setSelectedSignaturesReturn,
    getTaxReturns,
  } = useContext(AdminTaxReturnsContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { selectedCompany, refreshCompanyTaxReturns } =
    useContext(CompanyContext);

  const onToggleSignature = async (
    formIndex: number,
    signatureIndex: number,
  ) => {
    try {
      setIsLoading(true);

      const updatedForms = selectedSignaturesReturn?.e_sign_forms.map(
        (form, fIndex) => {
          if (fIndex === formIndex) {
            return {
              ...form,
              signatures: form.signatures?.map((sig, sIndex) => {
                if (sIndex === signatureIndex) {
                  return {
                    ...sig,
                    isMarkedSigned: !sig.isMarkedSigned,
                  };
                }
                return sig;
              }),
            };
          }
          return form;
        },
      );

      const { error } = await updateTaxReturn({
        id: selectedSignaturesReturn?.id!,
        companyId: selectedSignaturesReturn?.company_id!,
        token,
        ...{
          e_sign_forms: updatedForms,
        },
      });

      if (error) {
        addToast('error', 'Failed to update signature status');
      } else {
        setSelectedSignaturesReturn({
          ...selectedSignaturesReturn,
          e_sign_forms: updatedForms,
        } as TaxReturn);
        if (!!selectedCompany) {
          refreshCompanyTaxReturns();
        } else {
          getTaxReturns();
        }
        addToast('success', 'Signature status updated');
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onMarkAllSigned = async () => {
    try {
      setIsLoading(true);

      const updatedForms = selectedSignaturesReturn?.e_sign_forms?.map(
        (form) => ({
          ...form,
          signatures: form.signatures?.map((sig) => ({
            ...sig,
            isMarkedSigned: true,
          })),
        }),
      );

      const { error } = await updateTaxReturn({
        id: selectedSignaturesReturn?.id!,
        companyId: selectedSignaturesReturn?.company_id!,
        token,
        ...{
          e_sign_forms: updatedForms,
        },
      });

      if (error) {
        addToast('error', 'Failed to mark all signatures as signed');
      } else {
        setSelectedSignaturesReturn({
          ...selectedSignaturesReturn,
          e_sign_forms: updatedForms,
        } as TaxReturn);
        addToast('success', 'All signatures marked as signed');
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      getTaxReturns();
      setIsLoading(false);
    }
  };

  const hasUnsignedSignatures = selectedSignaturesReturn?.e_sign_forms?.some(
    (form) =>
      form.signatures?.some((sig) => !sig.signatureData && !sig.isMarkedSigned),
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Tax Return Signatures">
      <Dialog.Content>
        <Flex isColumn gap={12}>
          <Alert type="info">
            <Text type="small" color="currentcolor" isBreakAll={true}>
              Only signature that has not been signed by client can be marked as
              signed/unsigned. Client can still sign the form after it is marked
              as signed to provide their signature.
            </Text>
          </Alert>

          {selectedSignaturesReturn?.e_sign_forms?.map((form, formIndex) => (
            <Flex key={form.form_id} isColumn gap={12}>
              <Text color="primary" fontWeight="bold">
                {form.form_name || form.custom_form_name}
              </Text>
              {form.signatures?.map((signature, signatureIndex) => (
                <Flex
                  key={`${signature.name}-${signatureIndex}`}
                  isYCentered
                  gap={6}
                  isBetween={true}
                >
                  <Flex gap={12} isYCentered>
                    <Text>{signature.name}</Text>
                    <Pill label={signature.title} intent="secondary" />
                  </Flex>
                  <Flex gap={8} isYCentered>
                    <Text
                      color={
                        !!signature.signatureData || signature.isMarkedSigned
                          ? 'success'
                          : 'caution'
                      }
                    >
                      {!!signature.signatureData || signature.isMarkedSigned
                        ? 'Signed'
                        : 'Not Signed'}
                    </Text>
                    <Toggle
                      isChecked={
                        !!signature.signatureData || signature.isMarkedSigned
                      }
                      onChange={() =>
                        onToggleSignature(formIndex, signatureIndex)
                      }
                      isDisabled={!!signature.signatureData}
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="secondary"
            isDisabled={isLoading}
            onClick={onClose}
            value="Cancel"
          />
          <Button
            intent="primary"
            isDisabled={isLoading || !hasUnsignedSignatures}
            onClick={onMarkAllSigned}
            value="Mark all signed"
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
